// A row of pill buttons, one chosen (a radio group): short exclusive choices inside a form,
// in the style of the graph builder's condition types.
import type { ReactNode } from "react";

import { cn } from "./cn";

export function Segmented<K extends string | number>({
  label,
  options,
  value,
  onChange,
  className,
}: {
  label: string;
  options: readonly { key: K; label: string }[];
  value: K;
  onChange: (key: K) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={value === o.key}
          onClick={() => onChange(o.key)}
          className={cn(
            "rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors",
            value === o.key ? "border-primary bg-primary-lighter text-primary-dark" : "border-muted text-gray-700 hover:border-gray-300",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A form field's label and hint over a control that is not a single input. */
export function FieldSet({ legend, hint, children, className }: { legend: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <fieldset className={cn("min-w-0", className)}>
      <legend className="mb-2 text-sm font-medium text-gray-900">{legend}</legend>
      {children}
      {hint ? <p className="mb-0 mt-1.5 text-[13px] text-gray-500">{hint}</p> : null}
    </fieldset>
  );
}
